import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, LayoutDashboard, Database, Users, History, Link2 } from 'lucide-react';
const FEATURES = [{
  title: 'Natural-language formulas',
  description: 'Type what you want, get the exact formula. No more Googling syntax.',
  icon: <Sparkles size={20} className="text-emerald-400" data-id="element-32" />,
  className: 'md:col-span-2 md:row-span-2',
  visual: <div className="mt-6 p-4 bg-zinc-950 rounded-lg border border-white/10 font-mono text-sm" data-id="element-33">
        <div className="text-zinc-500 mb-2" data-id="element-34">{'// Prompt'}</div>
        <div className="text-emerald-400 mb-4" data-id="element-35">
          "Calculate MRR growth month over month"
        </div>
        <div className="text-zinc-500 mb-2" data-id="element-36">{'// Result'}</div>
        <div className="text-zinc-300" data-id="element-37">=IFERROR((B2-B1)/B1, 0)</div>
      </div>
}, {
  title: 'Boards from sheets',
  description: 'Instantly turn any data range into a live, shareable dashboard.',
  icon: <LayoutDashboard size={20} className="text-white" data-id="element-38" />,
  className: 'md:col-span-1 md:row-span-1',
  visual: <div className="mt-4 flex gap-2 h-20" data-id="element-39">
        <div className="flex-1 bg-white/5 rounded border border-white/10" data-id="element-40" />
        <div className="flex-1 bg-white/5 rounded border border-white/10" data-id="element-41" />
      </div>
}, {
  title: 'AI Data Cleaning',
  description: 'Fix formatting, extract entities, and deduplicate with one click.',
  icon: <Database size={20} className="text-white" data-id="element-42" />,
  className: 'md:col-span-1 md:row-span-1',
  visual: <div className="mt-4 space-y-2" data-id="element-43">
        <div className="h-6 w-full bg-red-500/10 border border-red-500/20 rounded flex items-center px-2 text-xs text-red-400 line-through" data-id="element-44">
          john doe gmail com
        </div>
        <div className="h-6 w-full bg-emerald-500/10 border border-emerald-500/20 rounded flex items-center px-2 text-xs text-emerald-400" data-id="element-45">
          john.doe@gmail.com
        </div>
      </div>
}, {
  title: 'Real-time collaboration',
  description: 'Work together seamlessly with multiplayer cursors and cell-level chat.',
  icon: <Users size={20} className="text-white" data-id="element-46" />,
  className: 'md:col-span-1 md:row-span-1',
  visual: null
}, {
  title: 'Connect any source',
  description: 'Live sync with Postgres, Stripe, Salesforce, or any REST API.',
  icon: <Link2 size={20} className="text-white" data-id="element-47" />,
  className: 'md:col-span-2 md:row-span-1 flex flex-row items-center gap-6',
  visual: <div className="flex items-center gap-3 mt-0" data-id="element-48">
        <div className="w-10 h-10 rounded bg-white/5 border border-white/10 flex items-center justify-center text-xs font-mono" data-id="element-49">
          DB
        </div>
        <div className="h-[1px] w-8 bg-white/20" data-id="element-50" />
        <div className="w-10 h-10 rounded bg-white/5 border border-white/10 flex items-center justify-center text-xs font-mono" data-id="element-51">
          API
        </div>
        <div className="h-[1px] w-8 bg-white/20" data-id="element-52" />
        <div className="w-10 h-10 rounded bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center text-xs font-mono text-emerald-400" data-id="element-53">
          S2B
        </div>
      </div>
}, {
  title: 'Version history',
  description: 'Git-like branching for your spreadsheets. Never lose a change.',
  icon: <History size={20} className="text-white" data-id="element-54" />,
  className: 'md:col-span-1 md:row-span-1',
  visual: null
}];
export function FeaturesBento() {
  return <section id="features" className="py-24 relative" data-id="element-55">
      <div className="max-w-7xl mx-auto px-6" data-id="element-56">
        <div className="mb-16" data-id="element-57">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" data-id="element-58">
            Everything you need. <br className="hidden md:block" data-id="element-59" />
            <span className="text-zinc-500" data-id="element-60">Nothing you don't.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[minmax(180px,auto)]" data-id="element-61">
          {FEATURES.map((feature, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className={`group relative bg-zinc-900/40 border border-white/10 rounded-2xl p-6 hover:bg-zinc-900/80 hover:border-white/20 transition-all duration-300 overflow-hidden ${feature.className}`} data-id="element-62">
              <div className="relative z-10 flex flex-col h-full" data-id="element-63">
                <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4" data-id="element-64">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-65">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed max-w-sm" data-id="element-66">
                  {feature.description}
                </p>
                {feature.visual && <div className="mt-auto pt-6" data-id="element-67">{feature.visual}</div>}
              </div>

              {/* Hover Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-68" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}